'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import CustomCalendar from './CustomCalendar';

const CalendarScheduler = ({
  itemId,
  itemName,
  buttonColor = '#007bff',
  userId,
  menuName = '',
}) => {
  const [scheduledDates, setScheduledDates] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Load existing schedule when component mounts
  useEffect(() => {
    if (itemId && userId) {
      loadSchedule();
    }
  }, [itemId, userId]);

  const loadSchedule = async () => {
    try {
      const response = await axios.get(`/api/calendar-schedule/${userId}/${itemId}`);
      if (!response.data.error) {
        setScheduledDates({
          [itemId]: response.data.schedule || {},
        });
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
  };

  const saveSchedule = async (schedule, preserveCurrentDate = null) => {
    try {
      setLoading(true);

      if (!userId || !itemId || !schedule) {
        console.error('Missing required fields for save:', { userId, itemId, schedule });
        alert('Error: Missing required data for saving schedule');
        return;
      }

      const payload = {
        userId: userId.toString(),
        itemId: itemId.toString(),
        schedule: schedule,
      };

      const response = await axios.post('/api/calendar-schedule', payload);

      if (response.data.error) {
        alert('Error saving schedule: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error saving schedule: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDateKey = date => {
    //
    const now = date;
    //  console.log('toISOString():', now.toISOString().split('T')[0])
    //  console.log('Local method:', `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`)
    // console.log('toISOString():', now.toISOString().split('T')[0])
    // console.log('Local method:', `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`)

    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const hasScheduledDates = itemId => {
    const schedule = scheduledDates[itemId];
    return schedule && Object.keys(schedule).length > 0;
  };

  const getScheduledDatesCount = itemId => {
    const schedule = scheduledDates[itemId];
    if (!schedule) return 0;
    return Object.values(schedule).filter(status => status === true).length;
  };

  const isPastDate = date => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly < today;
  };

  const getCurrentDayStatus = itemId => {
    const todayKey = formatDateKey(today);
    const schedule = scheduledDates[itemId];

    // Default to ON if no schedule is set for today
    if (!schedule || !(todayKey in schedule)) {
      return true; // Default to ON
    }
    return schedule[todayKey];
  };

  const currentStatus = getCurrentDayStatus(itemId);

  if (!itemId || !userId) {
    return null;
  }

  return (
    <>
      {/* Calendar Icon Button */}
      <span
        className="bi bi-circle-fill micon"
        style={{
          color: `${currentStatus ? 'green' : 'red'}`,
        }}
        title={`${currentStatus ? 'Active' : 'Not Active'}`}
      ></span>
      <span
        className={`bi ${hasScheduledDates(itemId) ? 'bi-calendar-check' : 'bi-calendar'} micon`}
        style={{
          color: hasScheduledDates(itemId) ? '#28a745' : '#6c757d',
          cursor: 'pointer',
        }}
        onClick={() => setIsOpen(!isOpen)}
        title="Active Calender Settings"
      ></span>

      {/* Current Day Status Indicator - with more spacing */}
      {/* {currentStatus !== null && (
        <div
          className="status-indicator"
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: currentStatus ? "#28a745" : "#dc3545",
            display: "inline-block",
            marginLeft: "10px",
            marginRight: "5px",
          }}
          title={`Today: ${currentStatus ? "ON" : "OFF"}`}
        ></div>
      )} */}

      {/* Calendar Modal */}
      {isOpen && (
        <div className="calendar-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="calendar-modal" onClick={e => e.stopPropagation()}>
            <div className="calendar-modal-header">
              <h4>
                {menuName && `${menuName} > `}
                {itemName.replace(/<br\s*\/?>/gi, ' ')}
              </h4>
              <button type="button" className="close-btn" onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>
            <CustomCalendar
              itemId={itemId}
              itemName={itemName}
              scheduledDates={scheduledDates}
              setScheduledDates={setScheduledDates}
              saveSchedule={saveSchedule}
              formatDateKey={formatDateKey}
              isPastDate={isPastDate}
              today={today}
              loading={loading}
              getScheduledDatesCount={getScheduledDatesCount}
            />
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10;
          border-radius: 8px;
        }

        .calendar-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .calendar-modal {
          background: white;
          border-radius: 8px;
          padding: 20px;
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          position: relative;
        }

        .calendar-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #eee;
          padding-bottom: 10px;
        }

        .calendar-modal-header h4 {
          margin: 0;
          font-size: 16px;
          color: #333;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #999;
        }

        .close-btn:hover {
          color: #333;
        }

        .calendar-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #eee;
          padding-bottom: 8px;
        }

        .nav-btn {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          font-size: 18px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          user-select: none;
          min-width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .nav-btn:hover {
          background-color: #e9ecef;
          border-color: #adb5bd;
        }

        .nav-btn:active {
          background-color: #dee2e6;
          border-color: #6c757d;
        }

        .nav-arrow {
          font-size: 20px;
          font-weight: bold;
          color: #495057;
          line-height: 1;
        }

        .month-title {
          margin: 0;
          font-size: 16px;
          font-weight: 500;
          user-select: none;
        }

        .week-controls {
          border: 1px solid #eee;
          border-radius: 6px;
          padding: 10px;
          background-color: #f9f9f9;
        }

        .week-controls-title {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 8px;
        }

        .week-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .week-btn {
          padding: 4px 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          font-size: 11px;
          transition: all 0.2s;
          user-select: none;
        }

        .week-btn:hover:not(.disabled) {
          background-color: #f0f0f0;
        }

        .week-btn.week-on {
          background-color: #28a745;
          color: white;
          border-color: #28a745;
        }

        .week-btn.week-off {
          background-color: #dc3545;
          color: white;
          border-color: #dc3545;
        }

        .week-btn.disabled {
          background-color: #f5f5f5;
          color: #999;
          cursor: not-allowed;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }

        .weekday-header {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 500;
          color: #666;
        }

        .empty-day {
          width: 32px;
          height: 32px;
        }

        .calendar-day {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          border-radius: 4px;
          transition: all 0.2s;
          cursor: pointer;
          user-select: none;
        }

        .future-on {
          background-color: #28a745;
          color: white;
        }

        .future-on:hover {
          background-color: #218838;
        }

        .future-off {
          background-color: #dc3545;
          color: white;
        }

        .future-off:hover {
          background-color: #c82333;
        }

        .past-on {
          background-color: #6f9f7f;
          color: #2d5a3d;
          cursor: not-allowed;
        }

        .past-off {
          background-color: #f8d7da;
          color: #721c24;
          cursor: not-allowed;
        }

        .today {
          font-weight: bold;
        }

        .calendar-legend {
          display: flex;
          justify-content: center;
          gap: 12px;
          font-size: 12px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .future-on-legend {
          background-color: #28a745;
        }

        .future-off-legend {
          background-color: #dc3545;
        }

        .past-on-legend {
          background-color: #6f9f7f;
        }

        .past-off-legend {
          background-color: #f8d7da;
        }

        .calendar-summary {
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #eee;
          padding-top: 8px;
        }

        .calendar-summary p {
          margin: 4px 0;
        }
        li.submenu-item div {
          margin-right: 0 !important;
        }
        .calendar-grid {
          border: 1px solid #eee;
          padding: 10px;
        }
      `}</style>
    </>
  );
};

export default CalendarScheduler;
